import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import groupsService from '@/api/services/groups';
import { getQueryClient } from '@/integrations/tanstack-query/RootProvider';
import type { GroupDetails, GroupUserRole } from '@/types/groups';
import type { QueryKey } from '@tanstack/react-query';
import { getGroupAssignmentsQueryKey } from './folder';
import { getGroupRoleSortOrder, resolveGroupUserRole } from '@/lib/groupRoles';
import { toast } from 'sonner';

const { queryClient } = getQueryClient();

export const getAllGroupsQueryKey = () => ['groups'] as QueryKey;
/**
 * Get groups query options
 * @returns Groups query options
 */
const getGroupsQueryOptions = () => {
  return queryOptions({
    queryKey: getAllGroupsQueryKey(),
    queryFn: ({ signal }) => groupsService.getAllGroups(signal),
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    select: (data) => {
      // Sort groups by index (ascending), then by name if index is the same
      const sortedData = (data?.data || [])?.sort((a, b) => {
        const indexA = a?.index ?? Infinity;
        const indexB = b?.index ?? Infinity;
        
        // Sort by index first
        if (indexA !== indexB) {
          return indexA - indexB;
        }
        
        // If index is the same, sort alphabetically by name
        return a?.name?.localeCompare(b?.name || '') || 0;
      });
      
      return {
        ...data,
        data: sortedData || data?.data,
      };
    },
  });
};

export const useGetAllGroups = () => {
  return useQuery(getGroupsQueryOptions());
};

export const getUsersByGroupQueryKey = (groupId: string) =>
  ['users-by-group', String(groupId)] as QueryKey;

export const useGetUsersByGroupOptions = (
  groupId: string,
  enabled = true,
  options?: { includeAll?: boolean; includePending?: boolean },
) => {
  return queryOptions({
    queryKey: getUsersByGroupQueryKey(groupId),
    enabled,
    queryFn: ({ signal }) => groupsService.getUsersByGroup(groupId, signal),
    select: (data) => {
      // const includeAll = options?.includeAll ?? false;
      const groupMembers = data?.data?.members;
      const filteredData = groupMembers?.sort((a, b) => {
        const roleA = resolveGroupUserRole(a);
        const roleB = resolveGroupUserRole(b);
        const roleDiff = getGroupRoleSortOrder(roleA) - getGroupRoleSortOrder(roleB);
        if (roleDiff !== 0) return roleDiff;
        return a?.user?.firstName?.localeCompare(b?.user?.firstName ?? '') ?? 0;
      });
      return {
        ...data,
        data: options?.includePending
          ? { members: filteredData, pendingInvites: data?.data?.pendingInvites }
          : filteredData,
      };
    },
  });
};

export const getAddMembersToGroupMutationKey = () => ['add-members-to-group'] as QueryKey;

// Fetch all users across all groups
export const getAllUsersByGroupQueryKey = () => ['users-by-group', 'all'] as QueryKey;

export const useGetAllUsersByGroupOptions = (
  enabled = true,
  options?: { includePending?: boolean },
) => {
  return queryOptions({
    queryKey: getAllUsersByGroupQueryKey(),
    enabled,
    queryFn: ({ signal }) => groupsService.getAllUsersByGroup(signal),
    select: (data) => {
      const users = (data?.data || []) as Array<any>;
      const sorted = users.slice().sort((a, b) => (a?.firstName || '').localeCompare(b?.firstName || ''));
      return { ...data, data: sorted };
    },
  });
};

export const useCreateNewGroupMutation = () => {
  return useMutation({
    mutationFn: (name: string) => groupsService.createNewGroup(name),
    onSuccess: (data) => {
      queryClient.setQueryData(['groups'], (old: { data: GroupDetails[] }) => {
        return {
          ...old,
          data: [...old.data, data.data],
        };
      });
    },
  });
};

export const getAddAdminsToGroupMutationKey = () => ['add-admins-to-group'] as QueryKey;

export const useAddMembersToGroupMutation = (folderId?: string) => {
  return useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: string; userIds: Array<number> }) =>
      groupsService.addMembersToGroup(groupId, userIds),
    mutationKey: getAddMembersToGroupMutationKey(),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: getUsersByGroupQueryKey(groupId) });
      if (folderId) {
        queryClient.invalidateQueries({
          queryKey: getGroupAssignmentsQueryKey({ groupId }),
        });
      }
    },
  });
};

export const getRemoveAdminsFromGroupMutationKey = () => ['remove-admins-from-group'] as QueryKey;
export const getRemoveMembersFromGroupMutationKey = () => ['remove-members-from-group'] as QueryKey;

export const useAddAdminsToGroupMutation = () => {
  return useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: string; userIds: Array<number> }) =>
      groupsService.addAdminsToGroup(groupId, userIds),
    mutationKey: getAddAdminsToGroupMutationKey(),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: getUsersByGroupQueryKey(groupId) });
    },
  });
};

export const useRemoveAdminsFromGroupMutation = () => {
  return useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: string; userIds: Array<number> }) =>
      groupsService.removeAdminsFromGroup(groupId, userIds),
    mutationKey: getRemoveAdminsFromGroupMutationKey(),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: getUsersByGroupQueryKey(groupId) });
    },
  });
};

export const useRemoveMembersFromGroupMutation = () => {
  return useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: string; userIds: Array<number> }) =>
      groupsService.removeMembersFromGroup(groupId, userIds),
    mutationKey: getRemoveMembersFromGroupMutationKey(),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: getUsersByGroupQueryKey(groupId) });
    },
    onError: (error) => {
      const sanitizedMessage = (error?.message || '')
        .replace(/\s*User IDs:\s*\[[^\]]*]\.?\s*/gi, '')
        .trim();
      toast.error(sanitizedMessage || 'Failed to remove members from group');
    },
  });
};

export const getUpdateGroupNameMutationKey = () => ['update-group-name'] as QueryKey;

export const useUpdateGroupNameMutation = () => {
  return useMutation({
    mutationFn: ({ groupId, name }: { groupId: string; name: string }) =>
      groupsService.updateGroupName(groupId, name),
    mutationKey: getUpdateGroupNameMutationKey(),
    onSuccess: ({ data }) => {
      queryClient.setQueryData(
        getAllGroupsQueryKey(),
        (old: { data: GroupDetails[] } | undefined) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.map((group) =>
              String(group.id) === String(data.id)
                ? { ...group, name: data.name }
                : group,
            ),
          };
        },
      );
    },
  });
};

export const getSortGroupsMutationKey = () => ['sort-groups'] as QueryKey;

export const useSortGroupsMutation = () => {
  return useMutation({
    mutationFn: (sortGroupData: Array<{ groupId: string; index: string }>) =>
      groupsService.sortGroups({ sortGroupData }),
    mutationKey: getSortGroupsMutationKey(),
    onSuccess: ({ data }) => {
      // Update query cache with all sorted groups
      queryClient.setQueryData(getAllGroupsQueryKey(), (old: { data: GroupDetails[] } | undefined) => {
        if (!old) return { status: true, data };
        return {
          ...old,
          data,
        };
      });
    },
  });
};

export const getChangeGroupUserRoleMutationKey = () => ['change-group-user-role'] as QueryKey;

/**
 * Applies a group role after a user has been added as a member.
 * READER is the default from add-users; ADMIN and EDITOR require update-role.
 */
export async function applyGroupMemberRole(
  groupId: string,
  userId: string | number,
  role: GroupUserRole,
): Promise<void> {
  if (role === 'READER') return;
  await groupsService.changeGroupUserRole(groupId, { userId, role });
}

export const useChangeGroupUserRoleMutation = () => {
  return useMutation({
    mutationFn: ({
      groupId,
      userId,
      role,
    }: {
      groupId: string;
      userId: string | number;
      role: GroupUserRole;
    }) => groupsService.changeGroupUserRole(groupId, { userId, role }),
    mutationKey: getChangeGroupUserRoleMutationKey(),
    onSuccess: (_, { groupId }) => {
      queryClient.invalidateQueries({ queryKey: getUsersByGroupQueryKey(groupId) });
      queryClient.invalidateQueries({
        queryKey: getGroupAssignmentsQueryKey({ groupId }),
      });
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to update member role');
    },
  });
};

export const getDeleteGroupMutationKey = () => ['delete-group'] as QueryKey;

/**
 * Delete a group. Caller must be a group admin.
 */
export const useDeleteGroupMutation = () => {
  return useMutation({
    mutationFn: (groupId: string) => groupsService.deleteGroup(groupId),
    mutationKey: getDeleteGroupMutationKey(),
    onSuccess: (_, groupId) => {
      queryClient.setQueryData(
        getAllGroupsQueryKey(),
        (old: { data: GroupDetails[] } | undefined) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: old.data.filter((group) => String(group.id) !== String(groupId)),
          };
        },
      );
      queryClient.invalidateQueries({ queryKey: getUsersByGroupQueryKey(groupId) });
    },
  });
};
